import React, { useEffect, useState } from "react";
import Dialog from "@components/Dialog";
import Input from "@components/Input";
import Text from "@components/Text";
import SizedBox from "@components/SizedBox";
import { Row } from "@components/Flex";
import Button from "@components/Button";
import Loading from "@components/Loading";
import styled from "@emotion/styled";
import type { AdminDeviceDetail } from "@services/adminService";

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 24px;
`;

const FieldWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Label = styled.label`
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  font-weight: bold;
  color: #494c5b;
`;

const Select = styled.select`
  height: 44px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-family: Nunito Sans, sans-serif;
  font-size: 14px;
  padding: 0 12px;
  color: #494c5b;
  outline: none;
  background: white;
  width: 100%;
  box-sizing: border-box;
`;

const TextArea = styled.textarea`
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-family: Nunito Sans, sans-serif;
  font-size: 14px;
  padding: 10px 12px;
  color: #494c5b;
  outline: none;
  resize: vertical;
  min-height: 80px;
  width: 100%;
  box-sizing: border-box;
`;

const DEVICE_TYPES: Record<number, string> = {
  1: "Retrofit",
  2: "CAN",
  3: "Potato",
};

const SENSOR_MODES: Record<number, string> = {
  1: "SENSOR",
  2: "FREQ",
  3: "AVG",
  4: "SUM",
};

interface Props {
  visible: boolean;
  device: AdminDeviceDetail["device"] | null;
  onClose: () => void;
  onSave: (updates: Record<string, any>) => Promise<void>;
}

const EditDeviceDialog: React.FC<Props> = ({
  visible,
  device,
  onClose,
  onSave,
}) => {
  const [alias, setAlias] = useState("");
  const [description, setDescription] = useState("");
  const [model, setModel] = useState("");
  const [deviceType, setDeviceType] = useState(1);
  const [width, setWidth] = useState("1");
  const [distance, setDistance] = useState("0");
  const [delay, setDelay] = useState("16000");
  const [sensorMode, setSensorMode] = useState(1);
  const [sensorMin, setSensorMin] = useState("0");
  const [sensorMax, setSensorMax] = useState("1000");
  // 0 = draw every fix that carries a position, matching the column default.
  const [minSatellitesCount, setMinSatellitesCount] = useState("0");
  const [isStaticDevice, setIsStaticDevice] = useState(false);
  const [fuelSensor, setFuelSensor] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (device && visible) {
      setAlias(device.alias ?? "");
      setDescription(device.description ?? "");
      setModel(device.model ?? "");
      setDeviceType(device.deviceType);
      setWidth(String(device.width ?? 1));
      setDistance(String(device.distance ?? 0));
      setDelay(String(device.delay ?? 16000));
      setSensorMode(device.sensorMode);
      setSensorMin(String(device.sensorMin ?? 0));
      setSensorMax(String(device.sensorMax ?? 1000));
      setMinSatellitesCount(String(device.minSatellitesCount ?? 0));
      setIsStaticDevice(device.isStaticDevice ?? false);
      setFuelSensor(device.fuelSensor ?? false);
    }
  }, [device, visible]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        alias,
        description,
        model,
        deviceType,
        sensorMode,
        sensorMin: Number(sensorMin),
        sensorMax: Number(sensorMax),
        width: Number(width),
        distance: Number(distance),
        delay: Number(delay),
        minSatellitesCount: Number(minSatellitesCount),
        isStaticDevice: deviceType === 3 ? isStaticDevice : false,
        fuelSensor,
      });
      onClose();
    } catch (e) {
      console.error("Failed to save device:", e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      destroyOnClose
      visible={visible}
      onClose={onClose}
      title="Edit"
      style={{ width: 640 }}
    >
      <FormGrid>
        <FieldWrap>
          <Label>Alias</Label>
          <Input
            value={alias}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setAlias(e.target.value)
            }
          />
        </FieldWrap>
        <FieldWrap>
          <Label>Sensor offset (meters)</Label>
          <Input
            type="number"
            value={distance}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setDistance(e.target.value)
            }
          />
        </FieldWrap>

        <FieldWrap>
          <Label>Device type</Label>
          <Select
            value={deviceType}
            onChange={(e) => setDeviceType(Number(e.target.value))}
          >
            {Object.entries(DEVICE_TYPES).map(([val, label]) => (
              <option key={val} value={val}>
                {label}
              </option>
            ))}
          </Select>
        </FieldWrap>
        <FieldWrap>
          <Label>Sensor delay (milliseconds)</Label>
          <Input
            type="number"
            value={delay}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setDelay(e.target.value)
            }
          />
        </FieldWrap>

        <FieldWrap>
          <Label>Harvester model</Label>
          <Input
            value={model}
            placeholder="Model"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setModel(e.target.value)
            }
          />
        </FieldWrap>
        <FieldWrap>
          <Label>Sensor mode</Label>
          <Select
            value={sensorMode}
            onChange={(e) => setSensorMode(Number(e.target.value))}
          >
            {Object.entries(SENSOR_MODES).map(([val, label]) => (
              <option key={val} value={val}>
                {label}
              </option>
            ))}
          </Select>
        </FieldWrap>

        <FieldWrap>
          <Label>Reaper width (meters)</Label>
          <Input
            type="number"
            value={width}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setWidth(e.target.value)
            }
          />
        </FieldWrap>
        <FieldWrap>
          <Label>Sensor minimum</Label>
          <Input
            type="number"
            value={sensorMin}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSensorMin(e.target.value)
            }
          />
        </FieldWrap>

        <FieldWrap>
          <Label>Description</Label>
          <TextArea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </FieldWrap>
        <div>
          <FieldWrap>
            <Label>Sensor maximum</Label>
            <Input
              type="number"
              value={sensorMax}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSensorMax(e.target.value)
              }
            />
          </FieldWrap>
          <SizedBox height={16} />
          <FieldWrap>
            <Label>Minimal satellite count</Label>
            <Input
              type="number"
              value={minSatellitesCount}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setMinSatellitesCount(e.target.value)
              }
            />
          </FieldWrap>
          {deviceType === 3 && (
            <>
              <SizedBox height={16} />
              <FieldWrap>
                <Label
                  htmlFor="admin-static-device"
                  style={{ display: "flex", alignItems: "center", gap: 8 }}
                >
                  <input
                    id="admin-static-device"
                    type="checkbox"
                    checked={isStaticDevice}
                    onChange={(e) => setIsStaticDevice(e.target.checked)}
                  />
                  Static device (no GPS)
                </Label>
                <Text size={12} color="#718096" style={{ marginTop: 4, lineHeight: 1.4 }}>
                  Analytics and metrics skip the speed filter; area uses a nominal 1 m/s for
                  yield. Track timestamps fall back to message time when per-point GPS time is
                  missing.
                </Text>
              </FieldWrap>
            </>
          )}
          <SizedBox height={16} />
          <FieldWrap>
            <Label
              htmlFor="admin-fuel-sensor"
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              <input
                id="admin-fuel-sensor"
                type="checkbox"
                checked={fuelSensor}
                onChange={(e) => setFuelSensor(e.target.checked)}
              />
              Fuel sensor connected
            </Label>
            <Text size={12} color="#718096" style={{ marginTop: 4, lineHeight: 1.4 }}>
              Records the fuel sensor input (mV, firmware GGSTR-1.6.7+) and shows it on Tracks
              and in Analytics. Readings are kept from the moment this is on.
            </Text>
          </FieldWrap>
        </div>
      </FormGrid>
      <SizedBox height={24} />
      <Row justifyContent="flex-end">
        <Button kind="primary" onClick={!saving ? handleSave : undefined} disabled={saving}>
          {saving ? <Loading /> : "Save"}
        </Button>
        <SizedBox width={12} />
        <Button kind="outline" onClick={onClose}>
          Cancel
        </Button>
      </Row>
    </Dialog>
  );
};

export default EditDeviceDialog;
